import { useState, useEffect, use } from 'react';
import { useGuidedStudyByPassageData } from '@/data/study-guide/guided-study-by-passage-data-context';
import { useUserSettings } from '@/lib/hooks/use-user-settings';
import useUser from '@/lib/hooks/use-user';
import { updateSelectedPersona } from '@/lib/user-settings';
import { Analytics } from '@/lib/analytics';
import { ensureUser } from '@/lib/utils/utils';
import { AuthContext } from '@/lib/context';

export function useGuidedStudyPersona(passageId: string) {
    const { user } = useUser();
    const auth = use(AuthContext);
    const { settings, isLoading: isSettingsLoading, refresh: refreshSettings } = useUserSettings(user);
    const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);

    // Update selected persona when settings change
    useEffect(() => {
        if (settings?.selectedPersonaId) {
            setSelectedPersonaId(settings.selectedPersonaId);
        } else if (!isSettingsLoading) {
            // Default only when we know settings are NOT loading and have no ID
            setSelectedPersonaId('junior-explorer');
        }
    }, [settings?.selectedPersonaId, isSettingsLoading]);

    // Get guided study data
    const { data: guidedStudyByPassage, isLoading: isGuidedStudyLoading } = useGuidedStudyByPassageData(passageId);

    // Derived data
    const guidedStudy = guidedStudyByPassage?.studies.find(s => s.personaId === selectedPersonaId);
    const allStudies = guidedStudyByPassage?.studies || [];
    const isLoading = isGuidedStudyLoading || isSettingsLoading;

    const switchPersona = async (personaId: string) => {
        // Update local state immediately
        setSelectedPersonaId(personaId);

        // Save to user settings
        if (auth) {
            try {
                const activeUser = await ensureUser(auth);
                await updateSelectedPersona(activeUser.uid, personaId);
                await refreshSettings();
                Analytics.personaSelected(personaId, 'manual');
            } catch (error) {
                console.error('Failed to update persona preference:', error);
            }
        }
    };

    return {
        selectedPersonaId,
        guidedStudy,
        allStudies,
        isLoading,
        switchPersona
    };
}
